import { Link } from 'react-router-dom';
import { SectionHeader } from '@/components/ui';
import { cleanName } from '@/seo/text';
import { entityPath, type EntityRef } from '@/seo/urls';
import type { Category } from '@/types';
import type { NavCity } from '@/hooks/useNavCities';
import { categoryHubPath } from '@/seo/categoryHubs';
import { sitePath } from '@/seo/hubLinks';
import { useRouteLocale } from '@/hooks/useRouteLocale';

/** A category, with the backend's `urlPath` once the category hubs exist (plan W3.2). */
type LinkableCategory = Category & { urlPath?: string | null };

interface BlockLink {
  key: string;
  to: string;
  label: string;
}

function LinkBlock({ title, links }: { title: string; links: BlockLink[] }) {
  return (
    <section aria-label={title}>
      <SectionHeader title={title} />
      <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1">
        {links.map((link) => (
          <li key={link.key}>
            {/* py-1 makes the tap target taller than the text on touch screens. */}
            <Link
              to={link.to}
              className="inline-block py-1 text-sm font-semibold text-ink-secondary transition-colors duration-200 hover:text-primary-strong hover:underline"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export interface HomeLinkBlocksProps {
  /** Top-level categories (`useTopCategories`); only those with a hub (`urlPath`) are linked. */
  categories: readonly LinkableCategory[];
  /** The home page's popular brands (`usePopularBrands`). */
  brands: readonly (EntityRef & { name: string })[];
  /** Indexable city hubs (`useNavCities`, web plan W3.2). */
  cities?: readonly NavCity[];
}

/**
 * "Top categories", "Popular brands" and "Deals by city" as plain text links
 * (web plan W0.6 / W3.2): every entry is an `<a href>` a crawler can follow,
 * built from data the home page already loads. A block without entries is left
 * out, and so is the whole row when all are empty (still loading, failed or no
 * data). Until the category hubs exist (W3.2) no category has a crawlable
 * page, so only "Popular brands" (and city hubs once they are live) show.
 */
export function HomeLinkBlocks({ categories, brands, cities = [] }: HomeLinkBlocksProps) {
  const locale = useRouteLocale();
  // Each category links its hub (`categoryHubPath`), never `/search?categoryId=…`.
  const categoryLinks: BlockLink[] = categories.flatMap((category) => {
    const to = categoryHubPath(category, locale);
    const label = category.title?.trim() ?? '';
    return to && label ? [{ key: category.id, to, label }] : [];
  });

  const brandLinks: BlockLink[] = brands
    .map((brand) => ({
      key: brand.id,
      to: entityPath(brand, 'brand'),
      label: cleanName(brand.name) || brand.name.trim(),
    }))
    .filter((link) => link.label);

  const cityLinks: BlockLink[] = cities.flatMap((city) => {
    const to = sitePath(city.urlPath);
    const label = city.name?.trim() ?? '';
    return to && label ? [{ key: city.slug, to, label }] : [];
  });

  if (categoryLinks.length === 0 && brandLinks.length === 0 && cityLinks.length === 0) return null;

  return (
    <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
      {categoryLinks.length > 0 && <LinkBlock title="Top categories" links={categoryLinks} />}
      {brandLinks.length > 0 && <LinkBlock title="Popular brands" links={brandLinks} />}
      {cityLinks.length > 0 && <LinkBlock title="Deals by city" links={cityLinks} />}
    </div>
  );
}

export default HomeLinkBlocks;
